import FadeUp from '../components/FadeUp';
import ImagePlaceholder from '../components/ImagePlaceholder';

const productSections = [
  {
    category: 'Fresh Vegetables',
    desc: 'A daily selection of fresh, seasonal vegetables. From everyday staples to leafy greens and root vegetables, we stock what your kitchen needs.',
    bg: '#FFFFFF',
    products: [
      { name: 'Seasonal Leafy Greens', label: 'PHOTO: Fresh leafy greens such as spinach, silverbeet and lettuce' },
      { name: 'Root Vegetables', label: 'PHOTO: Root vegetables including kumara, carrots and potatoes' },
      { name: 'Fresh Herbs', label: 'PHOTO: Bunches of fresh herbs including coriander and mint' },
      { name: 'Seasonal Variety Pack', label: 'PHOTO: Mixed seasonal vegetables in a display basket' },
    ],
  },
  {
    category: 'Spices and Herbs',
    desc: 'A vast collection of whole and ground spices, dried herbs and spice blends sourced from across South Asia, the Middle East, Africa and beyond.',
    bg: '#FDF6EC',
    products: [
      { name: 'Whole Spices', label: 'PHOTO: Whole spices including cumin, cardamom and cloves in packets' },
      { name: 'Ground Spice Blends', label: 'PHOTO: Labelled packets of ground spice blends on shelf' },
      { name: 'Chilli Varieties', label: 'PHOTO: Various dried chilli products including flakes and powder' },
      { name: 'Aromatic Herbs', label: 'PHOTO: Dried aromatic herbs including turmeric, fenugreek and bay leaves' },
    ],
  },
  {
    category: 'International Groceries',
    desc: 'Authentic products from South Asian, Pacific Island, East Asian and Middle Eastern food traditions. Ingredients that let you cook the real thing.',
    bg: '#FFFFFF',
    products: [
      { name: 'Indian and South Asian', label: 'PHOTO: Indian grocery products including dals, flours and pickles' },
      { name: 'Pacific Island Products', label: 'PHOTO: Pacific island grocery items including coconut cream and taro' },
      { name: 'Asian Sauces and Condiments', label: 'PHOTO: Asian cooking sauces and condiments on shelf' },
      { name: 'Middle Eastern Pantry', label: 'PHOTO: Middle Eastern products including tahini and rose water' },
    ],
  },
  {
    category: 'Pantry Essentials',
    desc: 'All the dry goods, canned products and cooking staples you need. Quality everyday essentials at honest prices.',
    bg: '#FDF6EC',
    products: [
      { name: 'Rice and Grains', label: 'PHOTO: Bags of rice and other grains in various sizes' },
      { name: 'Lentils and Legumes', label: 'PHOTO: Packets of lentils, chickpeas and dried beans' },
      { name: 'Flours and Breadcrumbs', label: 'PHOTO: Various flours including besan and rice flour' },
      { name: 'Oils and Vinegars', label: 'PHOTO: Cooking oils and vinegar products on shelf' },
    ],
  },
];

const sectionStyles = {
  heroSection: {
    background: '#C0392B',
    padding: '100px 24px 64px',
    textAlign: 'center',
  },
  heroHeading: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 800,
    fontSize: 'clamp(36px, 5vw, 54px)',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  heroSub: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '18px',
    color: '#E67E22',
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
  sectionWrap: (bg) => ({
    background: bg,
    padding: '80px 24px',
  }),
  sectionInner: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  categoryHeading: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 800,
    fontSize: 'clamp(24px, 3vw, 34px)',
    color: '#C0392B',
    marginBottom: '10px',
  },
  categoryDesc: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    color: '#555',
    lineHeight: 1.7,
    marginBottom: '40px',
    maxWidth: '640px',
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '24px',
  },
  productCard: {
    background: '#FFFFFF',
    borderRadius: '14px',
    overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.07)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  productName: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    fontSize: '15px',
    color: '#1A1A1A',
    padding: '14px 16px 16px',
    textAlign: 'center',
  },
};

export default function Products() {
  return (
    <div style={{ paddingTop: '68px' }}>
      {/* Hero */}
      <section style={sectionStyles.heroSection} aria-label="Products page hero">
        <FadeUp>
          <h1 style={sectionStyles.heroHeading}>What We Stock</h1>
          <p style={sectionStyles.heroSub}>Fresh, Flavourful and From Around the World</p>
        </FadeUp>
      </section>

      {/* Product sections */}
      {productSections.map((section, si) => (
        <section
          key={section.category}
          style={sectionStyles.sectionWrap(section.bg)}
          aria-label={section.category}
        >
          <div style={sectionStyles.sectionInner}>
            <FadeUp>
              <h2 style={sectionStyles.categoryHeading}>{section.category}</h2>
              <p style={sectionStyles.categoryDesc}>{section.desc}</p>
            </FadeUp>
            <div style={sectionStyles.productGrid}>
              {section.products.map((product, pi) => (
                <FadeUp key={product.name} delay={pi * 0.08}>
                  <div
                    style={sectionStyles.productCard}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 12px 36px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <ImagePlaceholder label={product.label} height={160} borderRadius="14px 14px 0 0" />
                    <p style={sectionStyles.productName}>{product.name}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA banner */}
      <section style={{ background: '#1A1A1A', padding: '72px 24px', textAlign: 'center' }} aria-label="Visit call to action">
        <FadeUp>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            color: '#E67E22',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '10px',
          }}>
            Come In Store
          </p>
          <h2 style={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 800,
            fontSize: 'clamp(26px, 4vw, 38px)',
            color: '#FFFFFF',
            marginBottom: '12px',
          }}>
            Can't Find What You're Looking For?
          </h2>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            color: 'rgba(255,255,255,0.65)',
            marginBottom: '32px',
          }}>
            Pop in and our friendly team will help you find exactly what you need.
          </p>
          <a
            href="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#E67E22',
              color: '#1A1A1A',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '15px',
              padding: '14px 32px',
              borderRadius: '100px',
              textDecoration: 'none',
              transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#d4711e'; e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#E67E22'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Get in Touch &#8594;
          </a>
        </FadeUp>
      </section>
    </div>
  );
}
